import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import * as moment from 'moment';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { CampaignStatus } from 'src/app/models/campaign';
import { CampaignService } from 'src/app/services/campaign.service';

@Component({
    selector: 'app-campaign-pause-modal',
    templateUrl: './campaign-pause-modal.component.html',
    styleUrls: ['./campaign-pause-modal.component.scss']
})
export class CampaignPauseModalComponent implements OnInit {
    public step: number = 1;

    public campaignId: string;
    public status: CampaignStatus;
    public startDate: Date;
    public isLoaded: boolean = true;
    public endDate: Date;
    public minStartDate: Date;
    public today = new Date();
    public upcomingStatusDate: Date | null = null;
    public isAdminLivePause: boolean = false;
    public isAdmin: boolean = false;

    dateFilter = (d: Date | null): boolean => {
        const date = d || new Date();
        return date >= new Date();
    };

    public startsInLessThan2WorkingDays: boolean;

    constructor(
        public dialog: MatDialog,
        private dialogRef: MatDialogRef<CampaignPauseModalComponent>,
        private campaignService: CampaignService,
        private authenticationService: AuthenticationService,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.campaignId = data.campaignId;
        this.status = data.status;
        this.startDate = data.startDate;
        this.endDate = data.endDate;
        this.minStartDate = moment(this.today).add(2, 'd').toDate();
        this.isAdminLivePause = data.isAdminLivePause || false;
        this.isAdmin = this.authenticationService.isAdmin();

        // For admin LIVE or PARTIALLY_LIVE campaign pause, show direct confirmation (step 2)
        if (
            this.isAdminLivePause &&
            (this.status === CampaignStatus.LIVE || this.status === CampaignStatus.PARTIALLY_LIVE)
        ) {
            this.step = 2;
            this.upcomingStatusDate = new Date();
        } else if (![CampaignStatus.LIVE, CampaignStatus.PARTIALLY_LIVE].includes(this.status)) {
            this.isLoaded = false;
            this.step = 0;
            this.checkIfStartsInLessThan2WorkingDays();
        }
    }

    ngOnInit(): void {}

    checkIfStartsInLessThan2WorkingDays() {
        this.campaignService.startsInLessThan2WorkingDays(this.campaignId).subscribe(result => {
            this.startsInLessThan2WorkingDays = result;
            this.isLoaded = true;
        });
    }

    onUpcomingStatusDateChanged(date: Date | null): void {
        if (date) this.upcomingStatusDate = date;
    }

    isUpcomingDateWithinRange(): boolean {
        if (!this.upcomingStatusDate || !this.startDate || !this.endDate) {
            return false;
        }

        const upcoming = moment(this.upcomingStatusDate);
        const start = moment(this.today).add(1, 'd').toDate();
        const end = moment(this.endDate);

        const result = upcoming.isAfter(start) && upcoming.isSameOrBefore(end);
        return result;
    }

    canPauseCampaign(): boolean {
        // For admin LIVE or PARTIALLY_LIVE campaign pause, always allow
        if (
            this.isAdminLivePause &&
            (this.status === CampaignStatus.LIVE || this.status === CampaignStatus.PARTIALLY_LIVE)
        ) {
            return true;
        }

        return (
            (!this.startsInLessThan2WorkingDays &&
                [CampaignStatus.APPROVED, CampaignStatus.PARTIALLY_APPROVED].includes(
                    this.status
                )) ||
            ![CampaignStatus.APPROVED, CampaignStatus.PARTIALLY_APPROVED].includes(this.status)
        );
    }

    moveToStepTwo() {
        this.step = 2;
    }

    getTitle() {
        // For admin LIVE or PARTIALLY_LIVE campaign pause, show specific message
        if (this.isAdminLivePause && this.status === CampaignStatus.LIVE) {
            return 'The campaign is live, are you sure you want to pause it?';
        }

        if (this.isAdminLivePause && this.status === CampaignStatus.PARTIALLY_LIVE) {
            return 'The campaign is partially live, are you sure you want to pause it?';
        }

        let title = 'Are you sure you want to pause this campaign?';
        return title;
    }

    getPauseDescription() {
        // For admin LIVE or PARTIALLY_LIVE campaign pause, show no description (clean UI)
        if (
            this.isAdminLivePause &&
            (this.status === CampaignStatus.LIVE || this.status === CampaignStatus.PARTIALLY_LIVE)
        ) {
            return '';
        }

        let description = '';

        if (this.status === CampaignStatus.APPROVED && !this.startsInLessThan2WorkingDays) {
            description =
                'In case any supplier has already paid the municipality fees on your behalf, it will be charged separately. Are you sure you want to pause?';
        }

        if (this.status === CampaignStatus.APPROVED && this.startsInLessThan2WorkingDays) {
            description =
                'Your campaign will start in less than 2 working days, please contact the media connector support if you would like to pause it.';
        }

        if (
            this.status === CampaignStatus.PARTIALLY_APPROVED &&
            !this.startsInLessThan2WorkingDays
        ) {
            description =
                'In case any supplier has already paid the municipality fees on your behalf, it will be charged separately. Are you sure you want to pause?';
        }

        if (
            this.status === CampaignStatus.PARTIALLY_APPROVED &&
            this.startsInLessThan2WorkingDays
        ) {
            description =
                'Your campaign will start in less than 2 working days, please contact the media connector support if you would like to pause it.';
        }

        if (this.status === CampaignStatus.LIVE) {
            description =
                'Your campaign is currently live. Please note that relaunching the campaign will require 2 working days to take effect. Are you sure you want to pause? Note: If you need to pause it urgently, please contact the media connector support.';
        }

        if (this.status === CampaignStatus.PARTIALLY_LIVE) {
            description =
                'Your campaign is currently partially live. Please note that relaunching the campaign will require 2 working days to take effect. Are you sure you want to pause? Note: If you need to pause it urgently, please contact the media connector support.';
        }

        return description;
    }

    pauseCampaign(): void {
        // For admin LIVE or PARTIALLY_LIVE pause, call the API directly without date
        if (
            this.isAdminLivePause &&
            (this.status === CampaignStatus.LIVE || this.status === CampaignStatus.PARTIALLY_LIVE)
        ) {
            this.campaignService
                .updateCampaignStatus(this.campaignId, CampaignStatus.PAUSED)
                .subscribe(_ => {
                    this.dialogRef.close();
                });
            return;
        }

        // For other cases, include the upcoming status date
        this.campaignService
            .updateCampaignStatus(this.campaignId, CampaignStatus.PAUSED, this.upcomingStatusDate)
            .subscribe(_ => {
                this.dialogRef.close();
            });
    }
}
